import * as angular from 'angular';
import { Inject } from '@angular/core';

import { BrowserModule, EVENT_MANAGER_PLUGINS } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';

import { ClickModifiersPlugin } from './click-modifiers.plugin';

// Composants graphiques externes
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_AUTOCOMPLETE_SCROLL_STRATEGY, MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MAT_MENU_SCROLL_STRATEGY, MatMenuModule } from '@angular/material/menu';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTreeModule } from '@angular/material/tree';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { CdkScrollable, ScrollingModule } from '@angular/cdk/scrolling';
import { Overlay } from '@angular/cdk/overlay';

// Modules utilitaires Aurora
import { JSR310LocalDatesModule } from './shared/jsr310-localdates/jsr310-localdates.module';
import { SecurityModule } from './security/security.module';
import { EventsModule } from './technical-modules/events/event-bus.module';

// Module UI Aurora
import { AccountMenuModule } from './ui-modules/account-menu/account-menu.module';
import { AppRoutingModule } from './app-routing.module';
import { BatchsMonitorModule } from './ui-modules/batchs-monitor/batchs-monitor.module';
import { DataImportModule } from './ui-modules/data-import/data-import.module';
import { NavigationMenuModule } from './ui-modules/navigation-menu/navigation-menu.module';
import { PageStructureModule } from './ui-modules/page-structure/page-structure.module';
import { HeaderModule } from './ui-modules/header/header.module';
import { TestFunctionsModule } from './ui-modules/test-functions/test-functions.module';

// Modules metier Aurora
import { AccesModule } from './data-domains/acces/acces/acces.module';
import { AgenceModule } from './data-domains/structure/agence/agence.module';
import { AntenneModule } from './data-domains/structure/antenne/antenne.module';
import { ResponsablesModule } from './data-domains/humain/responsables/responsables.module';
import { ConstructeurModule } from './data-domains/constructeur/constructeur.module';
import { GroupementParcModule } from './data-domains/structure/groupement-parc/groupement-parc.module';
import { MessageCollationneModule } from './data-domains/message-collationne/message-collationne.module';
import { ModeleOuvrageModule } from './data-domains/structure/modele-ouvrage/modele-ouvrage.module';
import { OperationModule } from './data-domains/planification-maintenance/operation/operation.module';
import { OuvrageModule } from './data-domains/structure/ouvrage/ouvrage.module';
import { ParcModule } from './data-domains/structure/parc/parc.module';
import { DocumentSecuriteModule } from './data-domains/document-securite/document-securite.module';
import { PerimetresModule } from './data-domains/perimetres/perimetres.module';
import { SousParcModule } from './data-domains/structure/sous-parc/sous-parc.module';
import { StaticTypesModule } from './static-types/static-types.module';
import { TypeMainenanceModule } from './data-domains/planification-maintenance/type-maintenance/type-maintenance.module'
import { UserModule } from './data-domains/humain/user/user.module';
import { MemoModule } from './data-domains/memo/memo.module';

// Modules graphiques internes
import { DragDropGroupsModule } from './shared/drag-drop-groups/drag-drop-groups.module';
import { BootstrapPopupModule } from './shared/bootstrap-popup/bootstrap-popup.module';
import { CheckboxListModule } from './shared/checkbox-list/checkbox-list.module';
import { DialogsModule } from './shared/dialogs/dialogs.module';
import { FilterableSelectModule } from './shared/filterable-select/filterable-select.module';
import { PannelModule } from './shared/pannel/pannel.module';
import { SuggestableInputModule } from './shared/suggestable-input/suggestable-input.module';
import { MultiTextInputModule } from './ui-modules/multi-text-input/multi-text-input.module';

// Composants graphiques internes
import { DragDropTreeComponent } from './shared/drag-drop-tree/drag-drop-tree.component';
import { MultiSelectComponent } from './shared/multi-select/multi-select.component';
import { PaginationComponent } from './shared/pagination/pagination.component';
import { ScrollableModalComponent } from './shared/ajs-bridges/scrollable-modal.component';

// Pipes
import { FormattersModule } from './ui-modules/formatters/formatters.module';
import { FiltersModule } from './ui-modules/filters/filters.module';

// Services metier
import { DowngradableOperationService } from './data-domains/planification-maintenance/operation/services/downgradable-operation.service';
import { LigneModelePlanMaintenanceService } from './data-domains/planification-maintenance/ligne-modele-plan-maintenance/ligne-modele-plan-maintenance.service';
import { LignePlanMaintenanceService } from './data-domains/planification-maintenance/ligne-plan-maintenance/ligne-plan-maintenance.service';
import { PlanMaintenanceService } from './data-domains/planification-maintenance/plan-maintenance/plan-maintenance.service';

// Services techniques
import { DateService } from './shared/date-service/date.service';
import { HttpService } from './shared/ajs-bridges/http.service';
import { ObservableService } from './shared/ajs-bridges/observable.service';
import { PushNotificationsService } from './shared/notifications/push.notification.service';
import { PendingRequestsInterceptorProvider } from './technical-modules/http/interceptors/pending-requests.interceptor';
import { PendingRequestsService} from './technical-modules/http/services/pending-requests.service';
import { StandardErrorsInterceptorProvider } from './technical-modules/http/interceptors/standard-errors.interceptor';

// Injectables divers
import { MatPaginatorIntlFr } from './shared/pagination/paginator-intl-fr';

// Bridges avec la partie anguler
import { downgradeComponent, UpgradeModule } from '@angular/upgrade/static';
import { rootScopeServiceProvider } from './shared/ajs-bridges/ajs-providers.module';

import { ContextService } from './technical-modules/context/services/context.service';

const AURORA_DATE_FORMATS = {
	parse: {
		dateInput: 'L',
	},
	display: {
		dateInput: 'L',
		monthYearLabel: 'MM YYYY',
		dateA11yLabel: 'LL',
		monthYearA11yLabel: 'MMMM YYYY',
	}
}

@NgModule({
	imports: [
		BrowserModule,
		MatAutocompleteModule,
		MatDatepickerModule,
		MatDialogModule,
		MatIconModule,
		MatMenuModule,
		MatMomentDateModule, // Necessaire pour la bonne localisation du MatDatepicker
		MatPaginatorModule,
		MatProgressBarModule,
		MatSelectModule,
		MatSortModule,
		MatTableModule,
		MatTabsModule,
		MatTreeModule,
		NgxSliderModule,
		ScrollingModule,
		FormattersModule,
		FiltersModule,

		BrowserModule,
		BrowserAnimationsModule,
		DragDropModule,
		FormsModule,
		ReactiveFormsModule,
		HttpClientModule,
		UpgradeModule,
		
		// Modules UI Aurora
		AccountMenuModule,
		AppRoutingModule,
		BatchsMonitorModule,
		DataImportModule,
		NavigationMenuModule,
		HeaderModule,
		PageStructureModule,
		TestFunctionsModule,
		
		// Modules metier Aurora
		AccesModule,
		AgenceModule,
		AntenneModule,
		ResponsablesModule,
		ConstructeurModule,
		GroupementParcModule,
		MessageCollationneModule,
		ModeleOuvrageModule,
		OperationModule,
		OuvrageModule,
		ParcModule,
		PerimetresModule,
		DocumentSecuriteModule,
		SousParcModule,
		StaticTypesModule,
		TypeMainenanceModule,
		UserModule,
		MemoModule,
		
		// Modules outils Aurora
		EventsModule,
		JSR310LocalDatesModule,
		SecurityModule,

		// Modules graphiques Aurora
		BootstrapPopupModule,
		CheckboxListModule,
		DialogsModule,
		DragDropGroupsModule,
		FilterableSelectModule,
		PannelModule,
		SuggestableInputModule,
		MultiTextInputModule,
	],
	providers: [
		DowngradableOperationService,
		LigneModelePlanMaintenanceService,
		LignePlanMaintenanceService,
		PlanMaintenanceService,

		DateService,
		HttpService,
		ObservableService,
		PushNotificationsService,
		PendingRequestsInterceptorProvider,
		PendingRequestsService,
		StandardErrorsInterceptorProvider,

		rootScopeServiceProvider,

		// Configuration pour permettre de capturer des clicks avec des conditions speciales telles que Ctrl, Shit, Alt
		{
			provide: EVENT_MANAGER_PLUGINS,
			useClass: ClickModifiersPlugin,
			multi: true,
		},
		// Configuration pour empecher les autocomplete de rester ouverts lors d'un scrolling et de risquer ainsi de passer par dessus le header
		{
			provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
			useFactory: (overlay: Overlay) => (() => overlay.scrollStrategies.close()),
			deps: [Overlay]
		},
		// Configuration pour empecher les menu contextuels de rester ouverts lors d'un scrolling et de risquer ainsi de passer par dessus le header
		{
			provide: MAT_MENU_SCROLL_STRATEGY,
			useFactory: (overlay: Overlay) => (() => overlay.scrollStrategies.close()),
			deps: [Overlay]
		},
		// Configuration pour que les paginators affichent des libelles en francais
		{
			provide: MatPaginatorIntl,
			useClass: MatPaginatorIntlFr
		},
		// Configuration du format de date pour les dates picker Material
		{
			provide: MAT_DATE_LOCALE,
			useValue: 'fr-FR'
		},
		{
			provide: MAT_DATE_FORMATS,
			useValue: AURORA_DATE_FORMATS
		}
	],
	declarations: [
		DragDropTreeComponent,
		MultiSelectComponent,
		PaginationComponent,
		ScrollableModalComponent,
	],
	entryComponents: [
		DragDropTreeComponent,
		PaginationComponent,
		ScrollableModalComponent,
	]
})
export class AppModule {
	constructor(
		private upgrade: UpgradeModule,
		@Inject(ContextService) private contextService: ContextService
	) { }

	ngDoBootstrap() {
		// Evol 56736
		// Recuperer le hash present au demarrage de l'application pour qu'on puisse rediriger sur la bonne page une fois l'initialisation terminee
		let preinitUrl: string | null = window.location.href;
		let tokens = preinitUrl.split("#/");
		let hash: string | null = null;
		if (tokens.length > 1 && tokens[1].length > 0) {
			hash = tokens[1];
		}
		this.contextService.setRequestedHash(hash);
		
		// Realise le bootstraping du moteur AngularJS pour en faire la racine de l'application
		// Le module NgUpgrade se charge de rendre le dit moteur compatible avec le reste d'Angular
		this.upgrade.bootstrap(document.documentElement, ['auroraApp'], { strictDi: false });
	}
}

// Initialisation des directives pour usage dans les templates angular-js
angular.module('auroraApp') //
	.directive('cdkScrollable',
		downgradeComponent({ component: CdkScrollable })
	) //
	.directive('dragDropTree',
		downgradeComponent({ component: DragDropTreeComponent })
	) //
	.directive('pagination',
		downgradeComponent({ component: PaginationComponent })
	) //
	.directive('scrollableModal',
		downgradeComponent({ component: ScrollableModalComponent })
	)
;